import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';

interface BulkActionBarProps {
  /** Number of selected rows; the bar hides itself when this is 0. */
  count: number;
  /** Pre-translated count label, e.g. "3 selected". */
  label: ReactNode;
  /** Clears the current selection (the X button). */
  onClear: () => void;
  /** Accessible label for the clear (X) button. */
  clearLabel: string;
  /** Action buttons rendered after the divider. */
  children: ReactNode;
}

/**
 * Floating bulk-action bar, meant to live inside a `relative` wrapper around a
 * table. The `sticky bottom-0 h-0` shell takes up no layout height (so it adds
 * no empty space or scrollbar), while the absolutely-positioned pill hangs off
 * it. As a result the pill stays pinned near the bottom of the viewport while a
 * long table scrolls, then releases at the bottom of the table - never floating
 * over the pagination that follows the wrapper.
 *
 * The pill wraps and is capped to the viewport: consumers pass fully worded
 * action labels (e.g. "Enable existing exclusion (3)") whose length varies a lot
 * per locale, and an unwrapped row would otherwise push past the edge of the
 * embedded admin frame.
 */
export function BulkActionBar({ count, label, onClear, clearLabel, children }: BulkActionBarProps) {
  if (count <= 0) return null;

  return (
    <div className="pointer-events-none sticky bottom-0 z-20 h-0">
      <div className="pointer-events-auto absolute bottom-6 left-1/2 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-3 rounded-lg border bg-card px-4 py-2 shadow-lg">
        <span className="whitespace-nowrap text-sm font-medium">{label}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={onClear}
          aria-label={clearLabel}
        >
          <X className="size-4" />
        </Button>
        <div className="h-5 w-px bg-border" />
        {children}
      </div>
    </div>
  );
}
